/**
 * Shared types used across multiple option statement tabs
 */

import { TransactionType } from '../common';

// ========================================
// Core Data Models
// ========================================

export interface EmployeeBasicInfo {
  employeeId: number;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  fullName: string; // Calculated: firstName + lastName
}

// Base transaction interface
export interface BaseOptionTransaction {
  id: number;
  optionAgreementId: number;
  type: TransactionType;
  typeName: string;
  effectiveDate: string;
  notes?: string;
}

// Generic transaction (used by mapper for processing API responses)
export interface OptionTransaction extends BaseOptionTransaction {
  vestedOptions: number;
  unvestedOptions: number;
  sharesExercised: number;
  deltaVestedOptions: number;      // Delta for vesting transactions
  deltaUnvestedOptions: number;    // Delta for grant transactions
  deltaCashlessExercised: number;  // Delta for cashless exercises
  deltaPurchaseExercised: number;  // Delta for purchase exercises
  purchaseExercisedShares: number;
  priceAtExercise: number;
  proceeds: number;
  cost: number;
  netValue: number;
  vestPercentage?: number;
  vestAnniversary?: number;
  vestedBalance: number;
  unvestedBalance: number;
}

export interface OptionAgreement {
  optionAgreementId: number;
  issueDate: string;
  expirationDate: string;
  description: string;

  // Share counts
  totalShares: number;
  vestedShares: number;
  unvestedShares: number;
  exercisedShares: number;
  unexercisedShares: number;      // Calculated: totalShares - exercisedShares
  vestedRemaining: number;        // Calculated: vestedShares - exercisedShares (available to exercise)

  // Pricing
  strikePrice: number;
  currentPrice: number;

  // Calculated values for UI
  vestedPercentage: number;       // From database
  vestedValue: number;            // (currentPrice - strikePrice) * vestedRemaining
  priceDifference: number;        // currentPrice - strikePrice
  isInTheMoney: boolean;          // currentPrice > strikePrice

  // Exercise breakdown
  cashlessExercisedShares: number;
  purchaseExercisedShares: number;
  cashlessProceeds: number;
  purchaseCosts: number;
  netExerciseValue: number;       // cashlessProceeds - purchaseCosts

  // Vesting information
  nextVestDate: string | null;
  nextVestAmount: number | null;

  // Related data
  transactions: OptionTransaction[];  // All transactions (used by mapper)
  cashlessExerciseTransactions: any[];  // Will be properly typed in specific files
  purchaseExerciseTransactions: any[];
  activeTransactions: any[];

  // UI state helpers
  hasVestedOptions: boolean;       // vestedRemaining > 0
  hasUnvestedOptions: boolean;     // unvestedShares > 0
  hasExercisedOptions: boolean;    // exercisedShares > 0
  hasPurchaseTransactions: boolean;
}

// ========================================
// Enums and Types
// ========================================

export enum TabType {
  SUMMARY = 'summary',
  VESTED = 'vested',
  UNVESTED = 'unvested',
  EXERCISED = 'exercised',
  PENDING_REQUESTS = 'pending_requests',
  ACTIVITY = 'activity'
}